import React from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import EyeOffIcon from "@mui/icons-material/VisibilityOff";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { printReports } from "../../utils";

function IconButton({ children, title, onClick, disabled, isActive = false }) {
    return (
        <button
            type="button"
            title={title}
            onClick={onClick}
            disabled={disabled}
            className={`tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-border tw-text-xs tw-transition-colors ${
                isActive
                    ? "tw-border-blue-500 tw-bg-blue-50 tw-text-blue-600"
                    : "tw-border-slate-300 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50"
            } ${disabled ? "tw-opacity-50 tw-cursor-not-allowed" : ""}`}
        >
            {children}
        </button>
    );
}

export default function ReportActionBar({
    deviceList = [],
    selectedDeviceIndex = 0,
    onPrevDevice,
    onNextDevice,
    hideEmptyDevices = false,
    onHideEmptyDevicesChange,
    autoRefresh = false,
    onAutoRefreshChange,
    showThresholds = true,
    onShowThresholdsChange,
    loading = false,
    onRefresh,
    onFavoriteClick,
    onPrintClick,
    onDownloadCSV,
    printRef,
}) {
    // Handle both string device names and device objects
    const currentDevice = deviceList[selectedDeviceIndex];
    const currentDeviceName =
        typeof currentDevice === "string"
            ? currentDevice
            : currentDevice?.name || "No devices";
    const canGoPrev = selectedDeviceIndex > 0;
    const canGoNext = selectedDeviceIndex < deviceList.length - 1;

    const handleRefresh = () => {
        // Disable auto-refresh when manually refreshing
        if (autoRefresh) {
            onAutoRefreshChange?.(false);
        }
        onRefresh?.();
    };

    return (
        <div className="tw-flex tw-items-center tw-justify-between tw-relative">
            <div className="tw-flex tw-items-center tw-gap-2">
                {deviceList.length > 0 ? (
                    <>
                        <button
                            onClick={onPrevDevice}
                            disabled={!canGoPrev}
                            className="tw-p-1 hover:tw-bg-slate-100 tw-rounded disabled:tw-opacity-30"
                        >
                            <ChevronLeftIcon fontSize="small" />
                        </button>
                        <span className="tw-text-sm tw-font-medium tw-min-w-[150px] tw-text-center">
                            {currentDeviceName}
                        </span>
                        <button
                            onClick={onNextDevice}
                            disabled={!canGoNext}
                            className="tw-p-1 hover:tw-bg-slate-100 tw-rounded disabled:tw-opacity-30"
                        >
                            <ChevronRightIcon fontSize="small" />
                        </button>
                    </>
                ) : (
                    <span className="tw-text-sm tw-text-slate-500 tw-px-2">
                        No devices
                    </span>
                )}
            </div>

            {/* Right: All control buttons */}
            <div className="tw-flex tw-items-center tw-gap-2">
                {/* Hide empty devices button */}
                <IconButton
                    title="Hide devices with no data"
                    onClick={() =>
                        onHideEmptyDevicesChange?.(!hideEmptyDevices)
                    }
                    isActive={hideEmptyDevices}
                >
                    <EyeOffIcon fontSize="small" />
                </IconButton>

                {/* Show thresholds button */}
                <IconButton
                    title="Use Thresholds"
                    onClick={() => onShowThresholdsChange?.(!showThresholds)}
                    isActive={showThresholds}
                >
                    <TrendingUpIcon fontSize="small" />
                </IconButton>

                {/* Vertical separator */}
                <div className="tw-h-6 tw-w-px tw-bg-slate-300"></div>

                {/* Auto-refresh button */}
                <IconButton
                    title="Auto-refresh every 60 seconds"
                    onClick={() => onAutoRefreshChange?.(!autoRefresh)}
                    isActive={autoRefresh}
                >
                    <ScheduleIcon fontSize="small" />
                </IconButton>

                {/* Manual refresh button */}
                <IconButton
                    title="Refresh Data"
                    onClick={handleRefresh}
                    disabled={loading}
                >
                    <RefreshIcon
                        fontSize="small"
                        className={loading ? "tw-animate-spin" : ""}
                    />
                </IconButton>

                {/* Vertical separator */}
                <div className="tw-h-6 tw-w-px tw-bg-slate-300"></div>

                {/* Favorite button */}
                <IconButton
                    title="Save as Snapshot"
                    onClick={() => onFavoriteClick?.()}
                    disabled={loading}
                >
                    <FavoriteBorderIcon fontSize="small" />
                </IconButton>

                {/* Vertical separator */}
                <div className="tw-h-6 tw-w-px tw-bg-slate-300"></div>

                {/* Print button */}
                <IconButton
                    title="Print report"
                    onClick={() => printReports(printRef?.current)}
                    disabled={loading}
                >
                    <PrintIcon fontSize="small" />
                </IconButton>

                {/* Download button */}
                <IconButton
                    title="Download CSV"
                    onClick={() => onDownloadCSV?.()}
                    disabled={loading}
                >
                    <DownloadIcon fontSize="small" />
                </IconButton>
            </div>
        </div>
    );
}
